<template>
    <div class="xero-settings xero-settings-container">
        <div class="xero-connection-card" :class="{'xero-connection-card--connected': originalCompanyInfo.activeXeroAuth, 'xero-connection-card--disconnected': !originalCompanyInfo.activeXeroAuth}">
                <div class="xero-connection-main">
                    <div class="xero-status">
                        <span class="xero-status__label">Xero status:</span>
                        <span class="xero-status__value">
                            <span class="xero-status__dot"></span>
                            <span v-if="originalCompanyInfo.activeXeroAuth">Connected to {{ selectedTenantName }}</span>
                            <span v-else>Not Connected</span>
                        </span>
                    </div>
                    <div v-show="originalCompanyInfo.activeXeroAuth" class="xero-company-select">
                        <label class="xero-form-label">Target Company:</label>
                        <multiselect
                                v-model="computedTenantId"
                                :options="computedTenants"
                                :showLabels="false"
                                :close-on-select="true"
                                :option-height="29"
                                :disabled="!isStatusActive"
                                style="opacity: 1"
                                :max-height="203"
                                placeholder="Select Xero Company"
                                track-by="key"
                                label="name"
                        ></multiselect>
                    </div>
                </div>
                <a v-show="!originalCompanyInfo.activeXeroAuth" href="#" class="xero-btn-primary" @click.prevent="connectToXero">Connect to Xero</a>
                <a v-show="originalCompanyInfo.activeXeroAuth" href="#" class="xero-btn-disconnect" @click.prevent="disconnectFromXero">Disconnect</a>
            </div>

            <div v-show="originalCompanyInfo.activeXeroAuth" class="xero-config-split">
                <nav class="xero-inner-sidebar">
                    <button type="button" class="xero-inner-nav-item" :class="{'active': activePane === 'invoices'}" @click="activePane = 'invoices'">
                        <span>Invoices (AR)</span>
                        <span>›</span>
                    </button>
                    <button type="button" class="xero-inner-nav-item" :class="{'active': activePane === 'bills'}" @click="activePane = 'bills'">
                        <span>Bills & Orders (AP)</span>
                        <span>›</span>
                    </button>
                    <button type="button" class="xero-inner-nav-item" :class="{'active': activePane === 'mapping'}" @click="activePane = 'mapping'">
                        <span>Supplier Account Mapping</span>
                        <span>›</span>
                    </button>
                </nav>

                <div class="xero-inner-content">
                    <section v-show="activePane === 'invoices'" class="xero-config-card">
                        <div class="xero-config-card__header">
                            <h2>Accounts Receivable (Invoices)</h2>
                        </div>
                        <div class="xero-config-card__body">
                            <div class="xero-grid-2-col">
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Create New Invoice - (Default)</label>
                                    <multiselect
                                            v-model="xeroAuth.defaultInvoiceStatus"
                                            :options="xeroStatuses.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(xeroStatuses, opt)"
                                            :showLabels="false"
                                            :disabled="!isStatusActive"
                                            style="opacity: 1"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Select one item"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Due Date - (Default)</label>
                                    <multiselect
                                            v-model="xeroAuth.invoiceDueDate"
                                            :options="invoiceDueDates.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(invoiceDueDates, opt)"
                                            :showLabels="false"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :disabled="!isStatusActive"
                                            style="opacity: 1"
                                            :max-height="203"
                                            placeholder="Default Due Date"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Invoice Reference Desc</label>
                                    <multiselect
                                            v-model="xeroAuth.invoiceReferenceDesc"
                                            :options="invoiceReferenceDescs.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(invoiceReferenceDescs, opt)"
                                            :showLabels="true"
                                            :disabled="!isStatusActive"
                                            style="opacity: 1"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Select Reference Desc"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Invoice GST Setting</label>
                                    <multiselect
                                            v-model="xeroAuth.defaultGstSetting"
                                            :options="xeroGstSettings.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(xeroGstSettings, opt)"
                                            :showLabels="false"
                                            :close-on-select="true"
                                            :disabled="!isStatusActive"
                                            style="opacity: 1"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Select Invoice GST Setting"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Complete Invoice Line Item Code</label>
                                    <input v-model="xeroAuth.defaultLineItemCode" type="text" :readonly="!isStatusActive" class="form-control xero-form-control" placeholder="Complete Invoice Line Item Code"/>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Excess Invoice Line Item Code</label>
                                    <input v-model="xeroAuth.defaultExcessLineItemCode" type="text" :readonly="!isStatusActive" class="form-control xero-form-control" placeholder="Excess Invoice Line Item Code"/>
                                </div>
                            </div>

                            <div class="xero-toggle-card">
                                <div class="xero-toggle-group">
                                    <div class="xero-toggle-text">
                                        <strong>Auto update Xero invoice</strong>
                                        <span>Automatically sync changes made in RepairShop to the corresponding Xero invoice.</span>
                                    </div>
                                    <label class="xero-switch">
                                        <input v-model="computedAutoUpdateInvoice" type="checkbox" :disabled="!isStatusActive" true-value="1" false-value="0">
                                        <span class="xero-slider"></span>
                                    </label>
                                </div>
                                <div class="xero-toggle-group">
                                    <div class="xero-toggle-text">
                                        <strong>Auto delete/void Xero invoice</strong>
                                        <span>If an invoice is voided here, automatically void the linked invoice in Xero.</span>
                                    </div>
                                    <label class="xero-switch">
                                        <input v-model="computedAutoDeleteInvoice" type="checkbox" :disabled="!isStatusActive" true-value="1" false-value="0">
                                        <span class="xero-slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section v-show="activePane === 'bills'" class="xero-config-card">
                        <div class="xero-config-card__header">
                            <h2>Accounts Payable (Purchase Orders & Bills)</h2>
                        </div>
                        <div class="xero-config-card__body">
                            <div class="xero-grid-2-col">
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Default Bill Status</label>
                                    <span class="xero-form-desc">When a part is received, create the bill in Xero as:</span>
                                    <multiselect
                                            v-model="xeroAuth.defaultBillStatus"
                                            :options="billStatuses.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(billStatuses, opt)"
                                            :showLabels="false"
                                            :disabled="!isStatusActive"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Default Bill Status"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Default Tax Rate (Bills)</label>
                                    <span class="xero-form-desc">Default tax applied to line items.</span>
                                    <multiselect
                                            v-model="xeroAuth.defaultBillTaxRate"
                                            :options="billTaxRates.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(billTaxRates, opt)"
                                            :showLabels="false"
                                            :disabled="!isStatusActive"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Default Tax Rate"
                                    ></multiselect>
                                </div>
                                <div class="xero-form-group">
                                    <label class="xero-form-label">Dedicated Returns Account (Optional)</label>
                                    <span class="xero-form-desc">Leave blank to use original expense account.</span>
                                    <multiselect
                                            v-model="xeroAuth.dedicatedReturnsAccount"
                                            :options="xeroAccounts.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(xeroAccounts, opt)"
                                            :showLabels="false"
                                            :disabled="!isStatusActive"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Dedicated Returns Account"
                                    ></multiselect>
                                </div>
                            </div>

                            <div class="xero-toggle-card">
                                <div class="xero-toggle-group">
                                    <div class="xero-toggle-text">
                                        <strong>Generate Xero Credit Notes for Returns</strong>
                                        <span>Automatically create a Supplier Credit Note in Xero for returned parts.</span>
                                    </div>
                                    <label class="xero-switch">
                                        <input v-model="computedGenerateCreditNotesForReturns" type="checkbox" :disabled="!isStatusActive" true-value="1" false-value="0">
                                        <span class="xero-slider"></span>
                                    </label>
                                </div>
                                <div class="xero-toggle-group">
                                    <div class="xero-toggle-text">
                                        <strong>Auto-Allocate Credits (On Account)</strong>
                                        <span>Automatically apply Credit Notes to the supplier's oldest unpaid bill.</span>
                                    </div>
                                    <label class="xero-switch">
                                        <input v-model="computedAutoAllocateCredits" type="checkbox" :disabled="!isStatusActive" true-value="1" false-value="0">
                                        <span class="xero-slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section v-show="activePane === 'mapping'" class="xero-config-card">
                        <div class="xero-config-card__header">
                            <h2>Supplier Account Mapping</h2>
                        </div>
                        <div class="xero-config-card__body">
                            <div class="xero-default-mapping-banner">
                                <div class="xero-banner-content">
                                    <div class="xero-banner-icon">i</div>
                                    <div class="xero-banner-text">
                                        <h3>Global Default Account for New Suppliers</h3>
                                        <p>Any supplier newly created or not explicitly mapped below will automatically route to this Xero account to ensure bills never fail to sync.</p>
                                    </div>
                                </div>
                                <div class="xero-banner-action">
                                    <multiselect
                                            v-model="xeroAuth.defaultSupplierAccountCode"
                                            :options="xeroAccounts.map(data => data.value)"
                                            :custom-label="opt => getOptionLabel(xeroAccounts, opt)"
                                            :showLabels="false"
                                            :disabled="!isStatusActive"
                                            :close-on-select="true"
                                            :option-height="29"
                                            :max-height="203"
                                            placeholder="Default Xero Account"
                                    ></multiselect>
                                </div>
                            </div>

                            <div class="xero-mapping-toolbar">
                                <input v-model="supplierSearch" type="text" class="form-control xero-form-control xero-search-input" placeholder="Search suppliers...">
                                <button type="button" class="xero-btn-outline" :disabled="!isStatusActive" @click="refreshXeroContacts">Refresh Xero Contacts</button>
                            </div>

                            <div class="xero-mapping-table-wrap">
                                <table class="xero-mapping-table">
                                    <thead>
                                    <tr>
                                        <th>App Supplier Name</th>
                                        <th>Linked Xero Contact</th>
                                        <th>Xero Expense Account Override</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="mapping in filteredSupplierMappings" :key="mapping.id">
                                        <td class="xero-supplier-name">{{ mapping.supplierName }}</td>
                                        <td>
                                            <multiselect
                                                    v-model="mapping.xeroContactId"
                                                    :options="xeroContacts.map(data => data.value)"
                                                    :custom-label="opt => getOptionLabel(xeroContacts, opt)"
                                                    :showLabels="false"
                                                    :disabled="!isStatusActive"
                                                    :close-on-select="true"
                                                    :option-height="29"
                                                    :max-height="203"
                                                    placeholder="Linked Xero Contact"
                                                    @input="saveSupplierMapping(mapping)"
                                            ></multiselect>
                                        </td>
                                        <td>
                                            <multiselect
                                                    v-model="mapping.expenseAccountCode"
                                                    :options="xeroAccounts.map(data => data.value)"
                                                    :custom-label="opt => getOptionLabel(xeroAccounts, opt)"
                                                    :showLabels="false"
                                                    :disabled="!isStatusActive"
                                                    :close-on-select="true"
                                                    :option-height="29"
                                                    :max-height="203"
                                                    placeholder="Xero Expense Account Override"
                                                    @input="saveSupplierMapping(mapping)"
                                            ></multiselect>
                                        </td>
                                    </tr>
                                    <tr v-if="filteredSupplierMappings.length === 0">
                                        <td colspan="3" class="xero-mapping-empty">No supplier mappings were returned by the backend.</td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import Axios from 'axios';
    import {appConfig} from '../../../config';
    import _ from 'lodash';
    import Multiselect from 'vue-multiselect';

    export default {
        name: 'Xero',
        components: {
            Multiselect
        },
        data() {
            return {
                xeroAuth: {
                    tenantId: null,
                    tenants: [],
                    defaultInvoiceStatus: null,
                    defaultGstSetting: null,
                    defaultLineItemCode: null,
                    defaultExcessLineItemCode: 'Invoice GST - Exclusive',
                    autoUpdateInvoice: null,
                    autoDeleteInvoice: 1,
                    invoiceReferenceDesc: null,
                    invoiceDueDate: null,
                    defaultBillStatus: null,
                    defaultBillTaxRate: null,
                    dedicatedReturnsAccount: null,
                    generateCreditNotesForReturns: 1,
                    autoAllocateCredits: 1,
                    defaultSupplierAccountCode: null,
                },
                xeroStatuses: [
                    {label: 'Draft', value: 'Draft'},
                    {label: 'Submitted', value: 'Submitted'},
                    {label: 'Authorised', value: 'Authorised'},
                ],
                invoiceReferenceDescs: [
                    {label: 'File Number', value: 'CardNumber'},
                    {label: 'Estimate Number', value: 'EstimateNumber'},
                    {label: 'Rego', value: 'Rego'},
                    {label: 'Insurer Name', value: 'InsurerName'},
                ],
                invoiceDueDates: [
                    {label: 'Today', value: 'Today'},
                    {label: 'In 7 Days', value: 'In7Days'},
                    {label: 'In 14 Days', value: 'In14Days'},
                    {label: 'In 30 days', value: 'In30days'},
                    {label: '1st day of Next Month', value: 'FirstDayOfNextMonth'},
                    {label: 'Last day of Next month', value: 'LastDayOfNextMonth'},
                ],
                xeroGstSettings: [
                    {label: 'Inclusive', value: 'Inclusive'},
                    {label: 'Exclusive', value: 'Exclusive'},
                ],
                xeroAuthEditable: false,
                xeroToggleVariants: [
                    {label: 'Enabled', value: 1},
                    {label: 'Disabled', value: 0},
                ],
                billStatuses: [
                    {label: 'Draft', value: 'Draft'},
                    {label: 'Awaiting Approval', value: 'AwaitingApproval'},
                    {label: 'Awaiting Payment (Authorised)', value: 'AwaitingPayment'},
                ],
                billTaxRates: [
                    {label: 'GST on Expenses (10%)', value: 'GSTOnExpenses'},
                    {label: 'BAS Excluded', value: 'BASExcluded'},
                ],
                xeroAccounts: [],
                xeroContacts: [],
                supplierMappings: [],
                supplierSearch: '',
                activePane: 'invoices',
                debouncedSave: null,
            };
        },
        computed: {
            ...mapGetters({
                // originalCompanyInfo: "currentCompany/getDetails",
                originalCompanyInfo: 'vendorInfo',
                isStatusActive: 'isStatusActive',
            }),
            computedAutoUpdateInvoice: {
                get() {
                    if (this.xeroAuth.autoUpdateInvoice == 1 || this.xeroAuth.autoUpdateInvoice === true) {
                        return 1;
                    }
                    return 0;
                },
                set(val) {
                    if (val == 1 || val === true) {
                        this.xeroAuth.autoUpdateInvoice = true;
                    } else {
                        this.xeroAuth.autoUpdateInvoice = false;
                    }
                    console.log('valval222', this.xeroAuth.autoUpdateInvoice)
                }
            },
            computedAutoDeleteInvoice: {
                get() {
                    if (this.xeroAuth.autoDeleteInvoice == 1 || this.xeroAuth.autoDeleteInvoice === true) {
                        return 1;
                    }
                    return 0;
                },
                set(val) {
                    if (val == 1 || val === true) {
                        this.xeroAuth.autoDeleteInvoice = true;
                    } else {
                        this.xeroAuth.autoDeleteInvoice = false;
                    }
                }
            },
            computedGenerateCreditNotesForReturns: {
                get() {
                    if (this.xeroAuth.generateCreditNotesForReturns == 1 || this.xeroAuth.generateCreditNotesForReturns === true) {
                        return 1;
                    }
                    return 0;
                },
                set(val) {
                    this.xeroAuth.generateCreditNotesForReturns = val == 1 || val === true;
                }
            },
            computedAutoAllocateCredits: {
                get() {
                    if (this.xeroAuth.autoAllocateCredits == 1 || this.xeroAuth.autoAllocateCredits === true) {
                        return 1;
                    }
                    return 0;
                },
                set(val) {
                    this.xeroAuth.autoAllocateCredits = val == 1 || val === true;
                }
            },
            computedTenants() {
                let tentants = [];
                _.forEach(this.xeroAuth.tenants, function (value, key) {
                    tentants.push({key: key, name: value});
                });
                return tentants;
            },
            computedTenantId: {
                get() {
                    let vm = this;
                    let result = _.find(this.computedTenants, function (i) {
                        return i.key == vm.xeroAuth.tenantId;
                    });
                    console.log(result);
                    return result || null;
                },
                set(val) {
                    this.xeroAuth.tenantId = val ? val.key : null;
                }
            },
            filteredSupplierMappings() {
                let search = (this.supplierSearch || '').toLowerCase();

                if (!search) {
                    return this.supplierMappings;
                }

                return this.supplierMappings.filter(mapping => {
                    return (mapping.supplierName || '').toLowerCase().indexOf(search) !== -1;
                });
            },
            selectedTenantName() {
                let tenant = this.computedTenantId;

                if (tenant && tenant.name) {
                    return tenant.name;
                }

                return 'Xero';
            },
        },
        created: function () {
            this.debouncedSave = _.debounce(this.saveXeroAuthData, 1000);
        },
        mounted() {
            this.loadXeroAuthData();
            this.loadXeroAccounts();
            this.loadXeroContacts();
            this.loadSupplierMappings();
        },
        methods: {
            getOptionLabel(options, value) {
                let option = (options || []).find(item => item.value == value);

                return option ? option.label : (value || '');
            },
            loadXeroAuthData() {
                Axios.get(`/fe/settings/get-xero-auth`)
                    .then(response => {
                        if (response.data && response.data._status) {
                            if (response.data.data) {
                                this.xeroAuth = Object.assign({}, this.getDefaultXeroAuth(), response.data.data);
                                setTimeout(() => {
                                    this.xeroAuthEditable = true;
                                }, 300);
                            } else {
                                this.xeroAuth = this.getDefaultXeroAuth();
                            }
                        }
                    })
                    .catch(e => console.error(e));
            },
            getDefaultXeroAuth() {
                return {
                    tenantId: null,
                    tenants: [],
                    defaultInvoiceStatus: null,
                    defaultGstSetting: null,
                    defaultLineItemCode: null,
                    defaultExcessLineItemCode: 'Invoice GST - Exclusive',
                    autoUpdateInvoice: 0,
                    autoDeleteInvoice: 0,
                    invoiceReferenceDesc: null,
                    invoiceDueDate: null,
                    defaultBillStatus: null,
                    defaultBillTaxRate: null,
                    dedicatedReturnsAccount: null,
                    generateCreditNotesForReturns: 1,
                    autoAllocateCredits: 1,
                    defaultSupplierAccountCode: null,
                };
            },
            saveXeroAuthData() {
                Axios.post(`/fe/settings/update-xero-auth`, this.xeroAuth)
                    .then(response => {
                        if (response.data && response.data._status) {
                            toastr.success('Xero settings has been updated successfully!');
                        }
                        else if (response?.data?._msg){
                          toastr.error(response?.data?._msg)
                        }
                    })
                    .catch(e => console.error(e));
            },
            connectToXero() {
                let wnd = window.open(`${appConfig.baseAPIURL}/settings/xero-oauth-start`, 'xero-auth-window', 'width=800,height=600,location=on');
                window.addEventListener('message', e => {
                    console.log(e);
                    let data = e.data;
                    if(data && typeof data !== "string") return;
                    if(data && data.source === 'vue-devtools-proxy') return;
                    if(data && data.source === 'vue-devtools-backend') return;
                    wnd.close();
                    data = JSON.parse(data);
                    console.log(data);

                    Axios.post(`/fe/settings/set-xero-auth`, data)
                        .then(response => {
                            if (response.data && response.data._status) {
                                this.$store.dispatch('loadVendorInfo', {}, {root: true});
                                this.loadXeroAuthData();
                            }
                        })
                        .catch(e => console.error(e));

                });
            },
            disconnectFromXero() {
                if (!this.isStatusActive) {
                  return;
                }
                Axios.post(`/fe/settings/remove-xero-auth`)
                    .then(response => {
                        if (response.data && response.data._status) {
                            this.$store.dispatch('loadVendorInfo', {}, {root: true});
                        }
                    })
                    .catch(e => console.error(e));
            },
            loadSupplierMappings() {
                Axios.get(`/fe/settings/xero/supplier-mappings`)
                    .then(response => {
                        if (response.data && response.data._status && Array.isArray(response.data.data)) {
                            this.supplierMappings = response.data.data.map(mapping => {
                                return {
                                    id: mapping.id,
                                    supplierId: mapping.supplierId,
                                    supplierName: mapping.supplierName,
                                    xeroContactId: mapping.xeroContactId || null,
                                    expenseAccountCode: mapping.expenseAccountCode || null,
                                };
                            });
                        }
                        else if (response?.data?._msg) {
                            toastr.error(response.data._msg);
                        }
                    })
                    .catch(e => console.error(e));
            },
            saveSupplierMapping(mapping) {
                if (!mapping || !mapping.supplierId) {
                    return;
                }

                let mappingId = mapping.id || mapping.supplierId;
                let payload = {
                    supplierId: mapping.supplierId,
                    xeroContactId: mapping.xeroContactId || null,
                    expenseAccountCode: mapping.expenseAccountCode || null,
                };

                Axios.post(`/fe/settings/xero/supplier-mappings/${mappingId}/update`, payload)
                    .then(response => {
                        if (response.data && response.data._status && response.data.data) {
                            let index = this.supplierMappings.findIndex(item => {
                                return item.id === mapping.id && item.supplierId === mapping.supplierId;
                            });

                            if (index !== -1) {
                                this.$set(this.supplierMappings, index, Object.assign({}, mapping, response.data.data));
                            }
                        }
                        else if (response?.data?._msg) {
                            toastr.error(response.data._msg);
                        }
                    })
                    .catch(e => console.error(e));
            },
            loadXeroAccounts() {
                Axios.get(`/fe/settings/xero/accounts`)
                    .then(response => {
                        if (response.data && response.data._status && Array.isArray(response.data.data)) {
                            this.xeroAccounts = response.data.data.map(account => {
                                return {
                                    label: account.label || `${account.code} - ${account.name}`,
                                    value: account.code,
                                };
                            });
                        }
                    })
                    .catch(e => console.error(e));
            },
            loadXeroContacts() {
                Axios.get(`/fe/settings/xero/contacts`)
                    .then(response => {
                        if (response.data && response.data._status && Array.isArray(response.data.data)) {
                            this.xeroContacts = response.data.data.map(contact => {
                                return {
                                    label: contact.label || contact.name,
                                    value: contact.id,
                                };
                            });
                        }
                    })
                    .catch(e => console.error(e));
            },
            refreshXeroContacts() {
                Axios.post(`/fe/settings/xero/contacts/refresh`, {tenantId: this.xeroAuth.tenantId})
                    .then(response => {
                        if (response.data && response.data._status && Array.isArray(response.data.data)) {
                            this.xeroContacts = response.data.data.map(contact => {
                                return {
                                    label: contact.label || contact.name,
                                    value: contact.id,
                                };
                            });
                        }
                        else if (response?.data?._msg) {
                            toastr.error(response.data._msg);
                        }
                    })
                    .catch(e => console.error(e));
            },
        },
        watch: {
            xeroAuth: {
                deep: true,
                handler() {
                    if (this.xeroAuthEditable) {
                       // this.debouncedSave();
                    }
                }
            },
        },
    };
</script>

<style scoped>
    .xero-settings-container {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding-top: 20px;
    }

    .xero-connection-card {
        background-color: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        margin-bottom: 24px;
        padding: 16px 24px;
    }

    .xero-connection-card--connected {
        border-left: 4px solid #10B981;
    }

    .xero-connection-card--disconnected {
        border-left: 4px solid #EF4444;
    }

    .xero-connection-main {
        display: flex;
        align-items: center;
        gap: 40px;
        flex: 1;
    }

    .xero-status,
    .xero-status__value,
    .xero-company-select {
        display: flex;
        align-items: center;
    }

    .xero-status {
        gap: 8px;
    }

    .xero-status__label {
        color: #64748B;
    }

    .xero-status__value {
        gap: 6px;
        font-weight: 500;
    }

    .xero-connection-card--connected .xero-status__value {
        color: #10B981;
    }

    .xero-connection-card--disconnected .xero-status__value {
        color: #EF4444;
    }

    .xero-status__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #EF4444;
    }

    .xero-connection-card--connected .xero-status__dot {
        background-color: #10B981;
    }

    .xero-company-select {
        gap: 15px;
        min-width: 360px;
    }

    .xero-btn-primary {
        background-color: #5D54EF;
        border: 1px solid #5D54EF;
        border-radius: 4px;
        color: #FFFFFF;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 18px;
        text-decoration: none;
    }

    .xero-btn-primary:hover {
        background-color: #4A43CC;
        border-color: #4A43CC;
        color: #FFFFFF;
        text-decoration: none;
    }

    .xero-btn-disconnect {
        background-color: #FFFFFF;
        border: 1px solid #EF4444;
        border-radius: 4px;
        color: #EF4444;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 7px 18px;
        text-decoration: none;
    }

    .xero-btn-disconnect:hover {
        background-color: #FEF2F2;
        color: #EF4444;
        text-decoration: none;
    }

    .xero-config-split {
        display: flex;
        gap: 24px;
        align-items: flex-start;
    }

    .xero-inner-sidebar {
        width: 240px;
        flex-shrink: 0;
        background-color: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
        padding: 12px 0;
    }

    .xero-inner-nav-item {
        width: 100%;
        background-color: transparent;
        border: none;
        border-left: 3px solid transparent;
        color: #64748B;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font: inherit;
        padding: 12px 20px;
        text-align: left;
        transition: 0.2s;
    }

    .xero-inner-nav-item:hover {
        background-color: #F4F6F8;
        color: #0F172A;
    }

    .xero-inner-nav-item.active {
        background-color: #EEF2FF;
        border-left-color: #5D54EF;
        color: #5D54EF;
        font-weight: 600;
    }

    .xero-inner-content {
        flex: 1;
        min-width: 0;
    }

    .xero-config-card {
        background-color: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 8px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
        overflow: visible;
    }

    .xero-config-card__header {
        background-color: #F8FAFC;
        border-bottom: 1px solid #E2E8F0;
        padding: 16px 24px;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
    }

    .xero-config-card__header h2 {
        color: #000000;
        font-size: 15px;
        font-weight: 600;
        margin: 0;
    }

    .xero-config-card__body {
        padding: 24px;
    }

    .xero-grid-2-col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
    }

    .xero-form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 20px;
    }

    .xero-form-label {
        color: #000000;
        font-weight: 600;
        margin: 0;
    }

    .xero-form-desc {
        color: #64748B;
        font-size: 11px;
    }

    .xero-form-control {
        border: 1px solid #E2E8F0;
        border-radius: 4px;
        color: #0F172A;
        outline: none;
        transition: 0.2s;
    }

    .xero-form-control:focus,
    .xero-search-input:hover {
        border-color: #5D54EF;
        box-shadow: 0 0 0 2px rgba(93, 84, 239, 0.1);
    }

    .xero-toggle-card {
        background-color: #F4F6F8;
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        margin-top: 10px;
        padding: 0 20px;
    }

    .xero-toggle-group {
        border-bottom: 1px solid #E2E8F0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        padding: 12px 0;
    }

    .xero-toggle-group:last-child {
        border-bottom: none;
    }

    .xero-toggle-text strong {
        color: #000000;
        display: block;
        font-weight: 600;
        margin-bottom: 2px;
    }

    .xero-toggle-text span {
        color: #64748B;
        font-size: 12px;
    }

    .xero-switch {
        display: inline-block;
        flex-shrink: 0;
        height: 22px;
        margin: 0;
        position: relative;
        width: 40px;
    }

    .xero-switch input {
        height: 0;
        opacity: 0;
        width: 0;
    }

    .xero-slider {
        background-color: #CBD5E1;
        border-radius: 22px;
        bottom: 0;
        cursor: pointer;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        transition: 0.3s;
    }

    .xero-slider:before {
        background-color: #FFFFFF;
        border-radius: 50%;
        bottom: 3px;
        content: "";
        height: 16px;
        left: 3px;
        position: absolute;
        transition: 0.3s;
        width: 16px;
    }

    .xero-switch input:checked + .xero-slider {
        background-color: #5D54EF;
    }

    .xero-switch input:checked + .xero-slider:before {
        transform: translateX(18px);
    }

    .xero-default-mapping-banner {
        background-color: #EEF2FF;
        border: 1px solid #C7D2FE;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        margin-bottom: 20px;
        padding: 16px 20px;
    }

    .xero-banner-content {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        flex: 1;
    }

    .xero-banner-icon {
        background-color: #FFFFFF;
        border-radius: 50%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        color: #5D54EF;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        height: 36px;
        width: 36px;
    }

    .xero-banner-text h3 {
        color: #000000;
        font-size: 14px;
        font-weight: 700;
        margin: 0 0 4px;
    }

    .xero-banner-text p {
        color: #64748B;
        font-size: 12px;
        line-height: 1.4;
        margin: 0;
    }

    .xero-banner-action {
        min-width: 250px;
    }
    .xero-banner-action >>> .multiselect{
      z-index: 2;
    }
    .xero-banner-action >>> .multiselect__content-wrapper{
      max-width: 250px;
    }
    .xero-banner-action >>> .multiselect__option,
    .xero-mapping-table >>> .multiselect__option{
      white-space: normal;
    }
    .xero-mapping-table >>> .multiselect__tags{
      overflow: hidden;
    }
    .xero-mapping-table >>> .multiselect--active .multiselect__select{
      top: 0 !important;
    }

    .xero-mapping-toolbar {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .xero-search-input {
        max-width: 300px;
    }

    .xero-btn-outline {
        background-color: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 4px;
        color: #0F172A;
        cursor: pointer;
        padding: 8px 15px;
        transition: 0.2s;
    }

    .xero-btn-outline:hover {
        border-color: #5D54EF;
        color: #5D54EF;
    }

    .xero-mapping-table-wrap {
        border: 1px solid #E2E8F0;
        border-radius: 6px;
        overflow: visible;
    }

    .xero-mapping-table {
        border-collapse: collapse;
        table-layout: fixed;
        width: 100%;
    }

    .xero-mapping-table th,
    .xero-mapping-table td {
        border-bottom: 1px solid #E2E8F0;
        padding: 12px;
        text-align: left;
    }

    .xero-mapping-table >>> .multiselect {
        box-sizing: border-box;
        width: 100%;
    }

    .xero-mapping-table >>> .multiselect--active {
        z-index: 50;
    }

    .xero-mapping-table >>> .multiselect__tags {
        box-sizing: border-box;
        min-width: 0;
        padding-right: 38px;
        position: relative;
        width: 100%;
    }

    .xero-mapping-table >>> .multiselect__single,
    .xero-mapping-table >>> .multiselect__input {
        box-sizing: border-box;
        display: block;
        max-width: 100%;
        min-width: 0;
        overflow: hidden;
        padding-right: 18px;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100% !important;
    }

    .xero-mapping-table >>> .multiselect__select,
    .xero-mapping-table >>> .multiselect--active .multiselect__select {
        background: transparent !important;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        right: 1px;
        top: 0;
        width: 36px;
        z-index: 1;
        padding: 0;
    }

    .xero-mapping-table >>> .multiselect__select::before {
        position: relative;
        top: auto;
        right: auto;
        margin-top: 0;
    }

    .xero-mapping-table >>> .multiselect__content-wrapper {
        box-sizing: border-box;
        left: 0;
        min-width: 0;
        width: 100%;
    }

    .xero-mapping-table >>> .multiselect__content {
        width: 100%;
    }

    .xero-mapping-table th {
        background-color: #F8FAFC;
        color: #64748B;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .xero-supplier-name {
        color: #000000;
        font-weight: 600;
    }

    .xero-mapping-empty {
        color: #64748B;
        text-align: center;
    }

    @media (max-width: 900px) {
        .xero-mapping-table-wrap {
            overflow-x: auto;
        }

        .xero-connection-card,
        .xero-connection-main,
        .xero-default-mapping-banner,
        .xero-mapping-toolbar {
            align-items: stretch;
            flex-direction: column;
        }

        .xero-company-select {
            align-items: stretch;
            flex-direction: column;
            min-width: 0;
        }

        .xero-config-split {
            flex-direction: column;
        }

        .xero-inner-sidebar {
            display: flex;
            overflow-x: auto;
            padding: 0;
            width: 100%;
        }

        .xero-inner-nav-item {
            border-left: none;
            border-bottom: 3px solid transparent;
            justify-content: center;
            min-width: max-content;
            white-space: nowrap;
        }

        .xero-inner-nav-item.active {
            border-bottom-color: #5D54EF;
            border-left-color: transparent;
        }

        .xero-grid-2-col {
            grid-template-columns: 1fr;
            gap: 10px;
        }

        .xero-banner-action,
        .xero-search-input {
            max-width: none;
            min-width: 0;
            width: 100%;
        }
    }

    .btn-danger {
        font-weight: bold;
        border-radius: 3px;
        font-size: 1rem;
    }

    .custom-select option {
        background-color: #5E6571 !important;

    }

</style>
